import type { DashboardAiSummary } from "@adpilot/shared";
import { IconChevronRight, IconClock, IconInfoCircle, IconListCheck } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DEFAULT_APPROVAL_SEARCH } from "@/lib/approval-search";
import { useFormatters } from "@/lib/format";

interface AiUsageSectionProps {
  summary: DashboardAiSummary;
}

type AiDashboardSectionsProps = AiUsageSectionProps;

interface UsageMetricProps {
  label: string;
  tooltip: string;
  value: string;
  detail: string;
}

function UsageMetric({ label, tooltip, value, detail }: UsageMetricProps) {
  return (
    <Card className="min-h-[94px] border-border/80 bg-background shadow-none">
      <CardHeader className="gap-1">
        <CardTitle className="flex items-center gap-1.5 text-[11px]! leading-4 font-semibold text-muted-foreground">
          <span>{label}</span>
          <Tooltip>
            <TooltipTrigger
              aria-label={tooltip}
              render={
                <button
                  type="button"
                  className="rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              }
            >
              <IconInfoCircle aria-hidden className="size-3" />
            </TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
          </Tooltip>
        </CardTitle>
      </CardHeader>
      <CardContent className="mt-auto space-y-1">
        <p className="font-mono text-[18px] leading-6 font-semibold tabular-nums tracking-tight">
          {value}
        </p>
        <p className="text-[9px] leading-4 text-muted-foreground">{detail}</p>
      </CardContent>
    </Card>
  );
}

interface AiNotificationsSectionProps {
  summary: DashboardAiSummary;
}

export function AiNotificationsSection({ summary }: AiNotificationsSectionProps) {
  const { t } = useTranslation("dashboard");
  const { formatDateTime, formatNumber } = useFormatters();
  const expiresAt = summary.nextApprovalExpiresAt
    ? formatDateTime(summary.nextApprovalExpiresAt)
    : "—";
  const approvalLinkLabel = t("xnurta.notifications.openAria", {
    count: summary.pendingDecisions,
    expiresAt,
  });

  return (
    <Card aria-labelledby="ai-notifications-title" data-density="compact" className="gap-3">
      <CardHeader className="items-center">
        <CardTitle className="flex items-center gap-2 text-[14px] font-semibold">
          <h2 id="ai-notifications-title">{t("xnurta.notifications.title")}</h2>
          <Badge variant={summary.pendingDecisions > 0 ? "destructive" : "neutral"} size="compact">
            {formatNumber(summary.pendingDecisions, { maximumFractionDigits: 0 })}
          </Badge>
        </CardTitle>
        <CardAction>
          <Button
            variant="ghost"
            size="xs"
            className="-mr-2 text-[10px] text-brand hover:text-brand"
            render={
              <Link
                to="/ai-operations"
                search={DEFAULT_APPROVAL_SEARCH}
                aria-label={approvalLinkLabel}
              />
            }
          >
            {t("xnurta.notifications.review")}
            <IconChevronRight aria-hidden className="size-3.5" />
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
        <Card className="min-h-[94px] border-border/80 bg-background shadow-none">
          <CardContent className="flex min-h-[70px] items-center gap-3">
            <span className="flex size-7 items-center justify-center rounded-[7px] bg-warning/10 text-warning-foreground">
              <IconListCheck aria-hidden className="size-3.5" />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] font-medium text-muted-foreground">
                {t("xnurta.notifications.pending")}
              </p>
              <p className="mt-0.5 font-mono text-[18px] leading-6 font-semibold tabular-nums text-ink">
                {formatNumber(summary.pendingDecisions, { maximumFractionDigits: 0 })}
              </p>
              <p className="text-[9px] leading-4 text-muted-foreground">
                {t("xnurta.notifications.pendingDetail")}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="min-h-[94px] border-border/80 bg-background shadow-none">
          <CardContent className="flex min-h-[70px] items-center gap-3">
            <span className="flex size-7 items-center justify-center rounded-[7px] bg-muted text-muted-foreground">
              <IconClock aria-hidden className="size-3.5" />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] font-medium text-muted-foreground">
                {t("xnurta.notifications.expiresAt")}
              </p>
              <p className="mt-0.5 truncate font-mono text-xs leading-6 font-semibold tabular-nums text-ink">
                {expiresAt}
              </p>
              <p className="text-[9px] leading-4 text-muted-foreground">
                {summary.nextApprovalExpiresAt
                  ? t("xnurta.notifications.expiresAtDetail")
                  : t("xnurta.notifications.noPending")}
              </p>
            </div>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
}

export function AiUsageSection({ summary }: AiUsageSectionProps) {
  const { t } = useTranslation("dashboard");
  const { formatNumber, formatPercent } = useFormatters();
  const optionalPercent = (value: number | null) =>
    value === null ? "—" : formatPercent(value, 2);
  const count = (value: number) => formatNumber(value, { maximumFractionDigits: 0 });

  return (
    <TooltipProvider>
      <Card aria-labelledby="ai-usage-title" data-density="compact" className="gap-3">
        <CardHeader>
          <CardTitle className="text-[14px] font-semibold">
            <h2 id="ai-usage-title">{t("xnurta.aiUsage.title")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-4">
          <UsageMetric
            label={t("xnurta.aiUsage.activationRate")}
            tooltip={t("xnurta.aiUsage.tooltips.activationRate")}
            value={optionalPercent(summary.enabledRate)}
            detail={t("xnurta.aiUsage.activationRateDetail")}
          />
          <UsageMetric
            label={t("xnurta.aiUsage.managedCampaigns")}
            tooltip={t("xnurta.aiUsage.tooltips.managedCampaigns")}
            value={count(summary.managedCampaigns)}
            detail={t("xnurta.aiUsage.managedCampaignsDetail")}
          />
          <UsageMetric
            label={t("xnurta.aiUsage.enabledCampaigns")}
            tooltip={t("xnurta.aiUsage.tooltips.enabledCampaigns")}
            value={count(summary.eligibleCampaigns)}
            detail={t("xnurta.aiUsage.enabledCampaignsDetail")}
          />
          <UsageMetric
            label={t("xnurta.aiUsage.executedActions")}
            tooltip={t("xnurta.aiUsage.tooltips.executedActions")}
            value={count(summary.executedActions)}
            detail={t("xnurta.aiUsage.executedActionsDetail")}
          />
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}

export function AiDashboardSections({ summary }: AiDashboardSectionsProps) {
  return (
    <div data-density="compact" className="flex flex-col gap-[14px]">
      <AiNotificationsSection summary={summary} />
      <AiUsageSection summary={summary} />
    </div>
  );
}
